import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

import { cx } from "../../Utils/helpers";

/**
 * Counts up to `value` the first time it scrolls into view.
 * <AnimatedCounter value={10} suffix="K+" />
 */
function AnimatedCounter({ value = 0, prefix = "", suffix = "", duration = 1800, decimals = 0, className, ...rest }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;

    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setDisplay(value);
      return undefined;
    }

    let frame;
    let start;

    const step = (timestamp) => {
      if (start === undefined) start = timestamp;

      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic

      setDisplay(value * eased);

      if (progress < 1) frame = window.requestAnimationFrame(step);
    };

    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [inView, value, duration]);

  return (
    <span ref={ref} className={cx("counter", className)} {...rest}>
      {prefix && <span className="counter__affix">{prefix}</span>}
      <span className="counter__value">{display.toFixed(decimals)}</span>
      {suffix && <span className="counter__affix">{suffix}</span>}
    </span>
  );
}

export default AnimatedCounter;
